import { v4 as uuid } from 'uuid';
import { appendReasoningSegment } from './thinking';
import { parseChoice } from './choice';
import { AgentApi, AgentApiError } from './api';
import {
  AgentFile,
  AgentMessage,
  AgentSession,
  Presentation,
  Snapshot,
  StreamEvent,
} from './model';
import { isGeneratedTitlePlaceholder } from './session-title';
import {
  parseAgentPageTask,
  type AgentPageTask,
  type AgentPageTaskProgress,
  type AgentPageTaskResult,
} from './page-task-protocol';

export const MAX_MESSAGE_LENGTH = 8000;
export const MAX_ATTACHMENTS = 10;
function uiWidgetType(raw: unknown): string | undefined {
  try {
    const payload = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (payload?.version !== 1 || !Array.isArray(payload.widgets) || payload.widgets.length !== 1)
      return undefined;
    const type = payload.widgets[0]?.type;
    return typeof type === 'string' ? type : undefined;
  } catch {
    return undefined;
  }
}
const composeText = (input: string, cards: { text: string }[]) =>
  [input, ...cards.map((card) => card.text)].filter(Boolean).join('\n\n');
export class AgentInputError extends Error {
  constructor(public key: 'agent.messageTooLong' | 'agent.tooManyFiles') {
    super(key);
  }
}

function normalizeMessage(message: AgentMessage): AgentMessage {
  const payload = message.ui_payload;
  const attachments =
    payload && typeof payload === 'object' && 'attachments' in payload
      ? (payload as { attachments?: unknown }).attachments
      : undefined;
  const uploads = Array.isArray(attachments)
    ? attachments.filter(
        (file): file is AgentFile =>
          file &&
          typeof file.id === 'string' &&
          typeof file.file_name === 'string' &&
          file.kind === 'UPLOAD' &&
          (file.status === 'READY' || file.status === 'PENDING'),
      )
    : [];
  return {
    ...message,
    attachments: [
      ...new Map(
        [...uploads, ...(message.attachments || [])].map((file) => [file.id, file]),
      ).values(),
    ],
  };
}

function sessionVersion(session: AgentSession) {
  return [
    session.last_message_at || '',
    session.last_active_at || '',
    session.created_at || '',
    String(session.has_messages),
  ].join(':');
}

function mergeRemoteMessages(
  messages: AgentMessage[],
  files: AgentFile[],
  previous: AgentMessage[] = [],
) {
  const previousById = new Map(previous.map((message) => [message.id, message]));
  return messages.map(normalizeMessage).map((message) => {
    const old = previousById.get(message.id);
    // The history endpoint can lag behind the terminal SSE event briefly. Never
    // regress a locally completed/failed/cancelled answer to the animated running state.
    const merged =
      old && old.status !== 'running' && message.status === 'running'
        ? { ...message, ...old }
        : {
            ...message,
            ...(old?.content === message.content
              ? { inputText: old.inputText, pastedTexts: old.pastedTexts }
              : {}),
            ui_payload: message.ui_payload ?? old?.ui_payload,
            page_task_result: message.page_task_result ?? old?.page_task_result,
            thinking_completed_at:
              message.thinking_completed_at ?? old?.thinking_completed_at,
          };
    return {
      ...merged,
      attachments: [
        ...new Map(
          [
            ...(merged.attachments || []),
            ...files.filter((file) => file.message_id === message.id),
          ].map((file) => [file.id, file]),
        ).values(),
      ],
    };
  });
}

const empty = (): Snapshot => ({
  sessions: [],
  sessionsById: {},
  page: 0,
  keyword: '',
  hasMore: false,
  uploading: 0,
  listLoading: false,
  listError: false,
  messages: {},
  drafts: {},
  pastedTexts: {},
  files: {},
  loading: {},
  errors: {},
  runs: {},
});

const SESSION_TITLE_POLL_INTERVAL_MS = 3000;
const SESSION_TITLE_POLL_MAX_ATTEMPTS = 20;
const wait = (milliseconds: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const abort = () => {
      clearTimeout(timer);
      reject(new Error('Aborted'));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', abort);
      resolve();
    }, milliseconds);
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) abort();
  });

/** One login-scoped service owns every surface; switching UI never submits another run. */
export function createAgentService(
  api: AgentApi,
  onNavigation: (payload: unknown, sessionId: string) => void | boolean | Promise<boolean> = () => {},
  onPageTask?: (task: AgentPageTask, sessionId: string) => void | Promise<void>,
) {
  let snapshot = empty();
  let disposed = false;
  let listVersion = 0;
  let creation: Promise<AgentSession> | undefined;
  const listeners = new Set<() => void>();
  const controllers = new Map<string, AbortController>();
  const pendingLoads = new Map<string, Promise<void>>();
  const loadedSessionVersions = new Map<string, string>();
  const preparing = new Set<string>();
  const pendingUi = new Map<string, unknown>();
  const pendingPageTasks = new Map<string, AgentPageTask>();
  const receivedPageTasks = new Set<string>();
  const pendingReads = new Map<string, Promise<void>>();
  const titlePolls = new Map<
    string,
    { timer: ReturnType<typeof setInterval>; attempts: number; inFlight: boolean }
  >();
  function update(change: Partial<Snapshot>) {
    if (disposed) return;
    snapshot = { ...snapshot, ...change };
    listeners.forEach((listener) => listener());
  }
  function mergeSession(session: AgentSession) {
    const sessions = [session, ...snapshot.sessions.filter((item) => item.id !== session.id)];
    sessions.sort(
      (a, b) =>
        Number(b.pinned) - Number(a.pinned) ||
        (b.last_message_at || b.last_active_at || '').localeCompare(
          a.last_message_at || a.last_active_at || '',
        ),
    );
    update({ sessions, sessionsById: { ...snapshot.sessionsById, [session.id]: session } });
  }
  function stopTitlePoll(id: string) {
    const poll = titlePolls.get(id);
    if (!poll) return;
    clearInterval(poll.timer);
    titlePolls.delete(id);
  }
  function stopAllTitlePolls() {
    [...titlePolls.keys()].forEach(stopTitlePoll);
  }
  async function pollGeneratedTitle(id: string) {
    const poll = titlePolls.get(id);
    if (!poll || poll.inFlight || disposed) return;
    const current = snapshot.sessionsById[id];
    if (!current || !isGeneratedTitlePlaceholder(current.title || undefined)) {
      stopTitlePoll(id);
      return;
    }
    poll.inFlight = true;
    poll.attempts += 1;
    try {
      const remote = await api.session(id);
      if (disposed || !titlePolls.has(id)) return;
      const latest = snapshot.sessionsById[id];
      if (!latest || !isGeneratedTitlePlaceholder(latest.title || undefined)) {
        stopTitlePoll(id);
        return;
      }
      if (!isGeneratedTitlePlaceholder(remote.title || undefined)) {
        stopTitlePoll(id);
        mergeSession({ ...latest, title: remote.title });
      } else if (poll.attempts >= SESSION_TITLE_POLL_MAX_ATTEMPTS) {
        stopTitlePoll(id);
      }
    } catch {
      if (poll.attempts >= SESSION_TITLE_POLL_MAX_ATTEMPTS) stopTitlePoll(id);
    } finally {
      const active = titlePolls.get(id);
      if (active) active.inFlight = false;
    }
  }
  function watchGeneratedTitle(id: string) {
    if (disposed || titlePolls.has(id)) return;
    const session = snapshot.sessionsById[id];
    if (!session || !isGeneratedTitlePlaceholder(session.title || undefined)) return;
    const poll = {
      timer: setInterval(() => void pollGeneratedTitle(id), SESSION_TITLE_POLL_INTERVAL_MS),
      attempts: 0,
      inFlight: false,
    };
    titlePolls.set(id, poll);
  }
  function patchMessage(
    id: string,
    messageId: string,
    fn: (message: AgentMessage) => AgentMessage,
  ) {
    update({
      messages: {
        ...snapshot.messages,
        [id]: (snapshot.messages[id] || []).map((m) => (m.id === messageId ? fn(m) : m)),
      },
    });
  }
  async function refresh(keyword = snapshot.keyword, more = false) {
    if (more && snapshot.listLoading) return;
    const version = ++listVersion;
    const page = more ? snapshot.page + 1 : 1;
    update({ keyword, listLoading: true, listError: false });
    try {
      const result = await api.list(page, keyword);
      if (version !== listVersion || disposed) return;
      const visible = result.sessions.filter((s) =>
        ['REAL_USER', 'OPERATOR', 'ONBOARDING'].includes(s.source),
      );
      const entries = more ? [...snapshot.sessions, ...visible] : visible;
      update({
        sessions: [...new Map(entries.map((s) => [s.id, s])).values()],
        sessionsById: {
          ...snapshot.sessionsById,
          ...Object.fromEntries(visible.map((s) => [s.id, s])),
        },
        page,
        hasMore: page * result.pageSize < result.total,
      });
    } catch {
      if (version === listVersion) update({ listError: true });
    } finally {
      if (version === listVersion) update({ listLoading: false });
    }
  }
  function finish(id: string) {
    const runs = { ...snapshot.runs };
    delete runs[id];
    update({ runs });
  }
  async function apply(id: string, record: StreamEvent): Promise<boolean> {
    const { event, payload } = record;
    const run = snapshot.runs[id];
    const sequence = payload.sequence;
    if (
      !run ||
      payload.message_id !== run.messageId ||
      typeof sequence !== 'number' ||
      !Number.isFinite(sequence)
    )
      return false;
    const progress = event === 'message.progress';
    if (sequence <= (progress ? run.progressSequence : run.eventSequence)) return false;
    update({
      runs: {
        ...snapshot.runs,
        [id]: {
          ...run,
          sequence: Math.max(run.sequence, sequence),
          reconnecting: false,
          ...(progress ? { progressSequence: sequence } : { eventSequence: sequence }),
        },
      },
    });
    const messageId = run.messageId;
    if (event === 'message.delta' && typeof payload.delta === 'string') {
      patchMessage(id, messageId, (m) => ({ ...m, content: m.content + payload.delta }));
    } else if (event === 'message.reasoning.delta' && typeof payload.delta === 'string') {
      patchMessage(id, messageId, (m) => ({
        ...m,
        reasoning: (m.reasoning || '') + payload.delta,
        reasoning_segments: appendReasoningSegment(
          m.reasoning_segments || [],
          sequence,
          payload.delta as string,
        ),
      }));
    } else if (
      event === 'message.progress' &&
      typeof payload.label === 'string' &&
      typeof payload.summary === 'string'
    ) {
      const progressValue = {
        label: payload.label,
        summary: payload.summary,
        steps: Array.isArray(payload.steps)
          ? payload.steps.filter((step): step is string => typeof step === 'string')
          : [],
      };
      patchMessage(id, messageId, (m) => ({ ...m, user_progress: progressValue }));
    } else if (event === 'message.ui') {
      const task = parseAgentPageTask(payload.ui_payload);
      if (task) {
        const key = `${id}:${task.id}`;
        if (!receivedPageTasks.has(key) && !pendingPageTasks.has(messageId)) {
          receivedPageTasks.add(key);
          pendingPageTasks.set(messageId, task);
        }
      } else if (
        parseChoice(payload.ui_payload) ||
        ['workspace_navigation', 'workspace_panel'].includes(uiWidgetType(payload.ui_payload) || '')
      ) {
        pendingUi.set(messageId, payload.ui_payload);
        patchMessage(id, messageId, (m) => ({ ...m, ui_payload: payload.ui_payload }));
      }
    } else if (event.startsWith('tool.')) {
      const tool = {
        id: `${messageId}:${sequence}`,
        event_type: event,
        tool_call_id: typeof payload.tool_call_id === 'string' ? payload.tool_call_id : undefined,
        tool_name: String(payload.tool_name || payload.tool || payload.name || ''),
        status: String(
          payload.status || (event === 'tool.started' ? 'running' : event.replace('tool.', '')),
        ),
        event_payload: payload,
        sequence,
        duration_ms:
          typeof payload.duration_ms === 'number'
            ? payload.duration_ms
            : typeof payload.duration === 'number'
            ? Math.round(payload.duration * 1000)
            : undefined,
        created_at: typeof payload.created_at === 'string' ? payload.created_at : undefined,
      };
      patchMessage(id, messageId, (m) => ({ ...m, tool_events: [...m.tool_events, tool] }));
    } else if (event === 'approval.request' && typeof payload.approval_id === 'string') {
      // Match Perseus: runtime commands are never exposed as an approval UI.
      try {
        await api.deny(id, messageId, payload.approval_id);
      } catch {
        await api.stop(id, messageId);
        throw new Error('Approval unavailable');
      }
    } else if (event === 'run.completed' || event === 'run.failed') {
      const output = [payload.output, payload.text, payload.content].find(
        (v) => typeof v === 'string' && v.trim(),
      );
      patchMessage(id, messageId, (m) => ({
        ...m,
        content: typeof output === 'string' ? output : m.content,
        status: event === 'run.completed' ? 'completed' : 'failed',
        thinking_completed_at:
          typeof payload.completed_at === 'string'
            ? payload.completed_at
            : new Date().toISOString(),
      }));
      update({ errors: { ...snapshot.errors, [id]: event === 'run.failed' } });
      finish(id);
      return true;
    }
    return false;
  }
  async function reconcile(id: string) {
    const [messages, session, files] = await Promise.all([
      api.messages(id),
      api.session(id),
      api.files(id),
    ]);
    if (disposed) return;
    const old = snapshot.messages[id] || [];
    // A late reconciliation must not erase a newly submitted run.
    if (snapshot.runs[id]) return;
    update({
      messages: {
        ...snapshot.messages,
        [id]: mergeRemoteMessages(messages, files, old),
      },
    });
    mergeSession(session);
    loadedSessionVersions.set(id, sessionVersion(session));
  }
  async function runStream(
    id: string,
    messageId: string,
    controller: AbortController,
    input?: NonNullable<Parameters<AgentApi['stream']>[3]>,
  ) {
    let first = input;
    let failures = 0;
    let disconnectedAt: number | undefined;
    let terminal = false;
    try {
      while (!disposed && !controller.signal.aborted && !terminal) {
        try {
          const cursor = snapshot.runs[id]?.sequence || 0;
          for await (const record of api.stream(id, messageId, controller.signal, first, cursor)) {
            if (controller.signal.aborted || disposed) return;
            const previous = snapshot.runs[id]?.sequence;
            terminal = await apply(id, record);
            if (snapshot.runs[id]?.sequence !== previous) {
              disconnectedAt = undefined;
              failures = 0;
            }
            if (terminal) break;
          }
          if (terminal) break;
        } catch (error) {
          if (controller.signal.aborted || disposed) return;
          // An explicit API rejection is not a dropped connection. The POST may have
          // failed before creating the agent message, so it must not enter resume.
          if (error instanceof AgentApiError && (error.status < 500 || error.code !== undefined))
            throw error;
        }
        // Never repeat a POST after an uncertain response. Resume by the original message id.
        first = undefined;
        disconnectedAt ??= Date.now();
        if (Date.now() - disconnectedAt > 60_000) throw new Error('Reconnect timeout');
        const run = snapshot.runs[id];
        if (!run) return;
        update({ runs: { ...snapshot.runs, [id]: { ...run, reconnecting: true } } });
        await wait(Math.min(8000, 500 * 2 ** Math.min(++failures, 4)), controller.signal);
      }
    } catch {
      if (!disposed && !controller.signal.aborted) {
        patchMessage(id, messageId, (m) => ({
          ...m,
          status: 'failed',
          thinking_completed_at: new Date().toISOString(),
        }));
        update({ errors: { ...snapshot.errors, [id]: true } });
        finish(id);
      }
    } finally {
      if (controllers.get(id) === controller) controllers.delete(id);
      const action = pendingUi.get(messageId);
      pendingUi.delete(messageId);
      const pageTask = pendingPageTasks.get(messageId);
      pendingPageTasks.delete(messageId);
      if (!disposed && !controller.signal.aborted) {
        if (
          terminal &&
          input &&
          !input.guidance &&
          snapshot.selectedId === id &&
          snapshot.messages[id]?.some((m) => m.id === messageId && m.status === 'completed')
        ) {
          if (pageTask && onPageTask && snapshot.selectedId === id) {
            let dependencyReady = !pageTask.afterActionId;
            const navigation = action && !parseChoice(action) ? action : undefined;
            if (pageTask.afterActionId && navigation) {
              try {
                const widget = typeof navigation === 'string' ? JSON.parse(navigation) : navigation;
                if (widget?.widgets?.[0]?.id === pageTask.afterActionId)
                  dependencyReady = (await onNavigation(navigation, id)) === true;
              } catch {
                dependencyReady = false;
              }
            }
            if (dependencyReady) await onPageTask(pageTask, id);
            else {
              const result: AgentPageTaskResult = {
                actionId: pageTask.id,
                status: 'blocked',
                reason: 'execution_failed',
                completedSteps: 0,
                modelCalls: 0,
                durationMs: 0,
              };
              update({ pageTask: {
                sessionId: id,
                taskId: pageTask.id,
                messageId,
                status: 'blocked',
                steps: [],
                result,
              } });
              patchMessage(id, messageId, (message) => ({
                ...message, page_task_result: result,
              }));
            }
          } else if (action && !parseChoice(action)) {
            await onNavigation(action, id);
          }
        }
        if (terminal)
          await reconcile(id).catch(() => update({ errors: { ...snapshot.errors, [id]: true } }));
      }
    }
  }
  function resume(id: string, messageId: string) {
    if (controllers.has(id) || disposed) return;
    const controller = new AbortController();
    controllers.set(id, controller);
    update({
      runs: {
        ...snapshot.runs,
        [id]: { messageId, sequence: 0, eventSequence: 0, progressSequence: 0, reconnecting: true },
      },
    });
    // History is already a partial snapshot; replay starts from zero to avoid appending it twice.
    patchMessage(id, messageId, (m) => ({
      ...m,
      content: '',
      reasoning: '',
      reasoning_segments: [],
      tool_events: [],
    }));
    void runStream(id, messageId, controller);
  }
  async function load(id: string) {
    if (snapshot.runs[id] || preparing.has(id)) return;
    if (pendingLoads.has(id)) return pendingLoads.get(id);
    const known = snapshot.sessionsById[id];
    if (
      snapshot.messages[id] &&
      known &&
      loadedSessionVersions.get(id) === sessionVersion(known)
    )
      return;
    update({
      loading: { ...snapshot.loading, [id]: true },
      errors: { ...snapshot.errors, [id]: false },
    });
    const pending = (async () => {
      try {
        const [session, messages, files] = await Promise.all([
          api.session(id),
          api.messages(id),
          api.files(id),
        ]);
        if (disposed) return;
        mergeSession(session);
        watchGeneratedTitle(id);
        const mergedMessages = mergeRemoteMessages(messages, files, snapshot.messages[id]);
        update({
          messages: { ...snapshot.messages, [id]: mergedMessages },
          files: { ...snapshot.files, [id]: files },
        });
        loadedSessionVersions.set(id, sessionVersion(session));
        const running = mergedMessages.find((m) => m.role === 'agent' && m.status === 'running');
        if (running) resume(id, running.id);
      } catch {
        update({ errors: { ...snapshot.errors, [id]: true } });
      } finally {
        pendingLoads.delete(id);
        update({ loading: { ...snapshot.loading, [id]: false } });
      }
    })();
    pendingLoads.set(id, pending);
    return pending;
  }
  function select(id?: string) {
    update({ selectedId: id, ...(snapshot.selectedId !== id ? { pageTask: undefined } : {}) });
    if (id) void load(id);
  }
  async function create() {
    if (!creation)
      creation = api.create().finally(() => {
        creation = undefined;
      });
    const session = await creation;
    if (disposed) throw new Error('Agent session ended');
    if (preparing.has('new')) preparing.add(session.id);
    mergeSession(session);
    const { new: pendingMessages, ...messages } = snapshot.messages;
    update({
      selectedId: session.id,
      messages: { ...messages, [session.id]: pendingMessages || [] },
      loading: { ...snapshot.loading, [session.id]: preparing.has(session.id), new: false },
      drafts: { ...snapshot.drafts, [session.id]: snapshot.drafts.new || '', new: '' },
      pastedTexts: {
        ...snapshot.pastedTexts,
        [session.id]: snapshot.pastedTexts.new || [],
        new: [],
      },
    });
    return session;
  }
  return {
    api,
    refresh,
    select,
    create,
    load,
    startPageTask(sessionId: string, taskId: string) {
      const messageId = [...(snapshot.messages[sessionId] || [])].reverse()
        .find((item) => item.role === 'agent')?.id;
      update({ pageTask: { sessionId, taskId, messageId, status: 'running', steps: [] } });
    },
    progressPageTask(progress: AgentPageTaskProgress) {
      const task = snapshot.pageTask;
      if (!task || task.status !== 'running') return;
      update({ pageTask: {
        ...task,
        progress,
        steps: progress.status === 'running' ? task.steps : [...task.steps, progress].slice(-40),
      } });
    },
    finishPageTask(result: AgentPageTaskResult) {
      const task = snapshot.pageTask;
      if (!task || task.taskId !== result.actionId) return;
      update({ pageTask: { ...task, status: result.status, result, progress: undefined } });
      if (task.messageId) {
        patchMessage(task.sessionId, task.messageId, (message) => ({
          ...message, page_task_result: result,
        }));
      }
    },
    read(id: string) {
      if (pendingReads.has(id)) return pendingReads.get(id)!;
      if (!snapshot.sessionsById[id]?.has_unread_agent_result) return Promise.resolve();
      const messages = snapshot.messages[id];
      const pending = api
        .read(id)
        .then(() => {
          // A read response for an older result must not clear a newer output.
          if (disposed || snapshot.messages[id] !== messages) return;
          const session = snapshot.sessionsById[id];
          if (session) mergeSession({ ...session, has_unread_agent_result: false });
        })
        .finally(() => pendingReads.delete(id));
      pendingReads.set(id, pending);
      return pending;
    },
    getSnapshot: () => snapshot,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    draft(text: string) {
      update({ drafts: { ...snapshot.drafts, [snapshot.selectedId || 'new']: text } });
    },
    pasteText(text: string, cardId?: string) {
      if (!text) return;
      const key = snapshot.selectedId || 'new';
      const cards = snapshot.pastedTexts[key] || [];
      const next = cardId
        ? cards.map((card) => (card.id === cardId ? { ...card, text } : card))
        : [...cards, { id: uuid(), text }];
      if (composeText(snapshot.drafts[key] || '', next).length > MAX_MESSAGE_LENGTH)
        throw new AgentInputError('agent.messageTooLong');
      update({ pastedTexts: { ...snapshot.pastedTexts, [key]: next } });
    },
    removePastedText(cardId: string) {
      const key = snapshot.selectedId || 'new';
      update({
        pastedTexts: {
          ...snapshot.pastedTexts,
          [key]: (snapshot.pastedTexts[key] || []).filter((card) => card.id !== cardId),
        },
      });
    },
    async upload(file: File) {
      const key = snapshot.selectedId || 'new';
      if ((snapshot.files[key]?.length || 0) + snapshot.uploading >= MAX_ATTACHMENTS)
        throw new AgentInputError('agent.tooManyFiles');
      update({ uploading: snapshot.uploading + 1 });
      try {
        const id = snapshot.selectedId || (await create()).id;
        const uploaded = await api.upload(id, file);
        update({ files: { ...snapshot.files, [id]: [...(snapshot.files[id] || []), uploaded] } });
      } finally {
        update({ uploading: Math.max(0, snapshot.uploading - 1) });
      }
    },
    removeFile(id: string) {
      const key = snapshot.selectedId || 'new';
      update({
        files: { ...snapshot.files, [key]: (snapshot.files[key] || []).filter((f) => f.id !== id) },
      });
    },
    async send(presentation: Presentation, locale: string, pageContext?: unknown, reply?: string) {
      if (snapshot.uploading || snapshot.pageTask?.status === 'running') return;
      const originalId = snapshot.selectedId;
      const draftKey = originalId || 'new';
      const draftText = reply ?? snapshot.drafts[draftKey] ?? '';
      const pastedTexts = reply === undefined ? snapshot.pastedTexts[draftKey] || [] : [];
      const composedText = composeText(draftText, pastedTexts);
      // A choice reply may be retried immediately after prepare failed while the
      // initial load's finally block is still settling its loading flag.
      if (
        preparing.has(draftKey) ||
        snapshot.runs[draftKey] ||
        (reply === undefined && snapshot.loading[draftKey])
      )
        return;
      const files = reply === undefined ? snapshot.files[draftKey] || [] : [];
      if (!composedText.trim() && !files.length) return;
      if (composedText.length > MAX_MESSAGE_LENGTH)
        throw new AgentInputError('agent.messageTooLong');
      if (files.length > MAX_ATTACHMENTS) throw new AgentInputError('agent.tooManyFiles');
      const text = composedText.trim() ? composedText : 'Please review the uploaded files';
      const userMessageId = uuid();
      const created_at = new Date().toISOString();
      let id = draftKey;
      // 本地回显先于会话创建和沙箱准备，让首条消息也能立即清空输入框。
      preparing.add(draftKey);
      update({
        pageTask: undefined,
        messages: {
          ...snapshot.messages,
          [draftKey]: [
            ...(snapshot.messages[draftKey] || []),
            {
              id: userMessageId,
              role: 'user',
              content: text,
              ...(pastedTexts.length ? { inputText: draftText, pastedTexts } : {}),
              status: 'running',
              created_at,
              tool_events: [],
              attachments: files,
            },
          ],
        },
        drafts: reply === undefined ? { ...snapshot.drafts, [draftKey]: '' } : snapshot.drafts,
        pastedTexts:
          reply === undefined ? { ...snapshot.pastedTexts, [draftKey]: [] } : snapshot.pastedTexts,
        files: reply === undefined ? { ...snapshot.files, [draftKey]: [] } : snapshot.files,
        loading: { ...snapshot.loading, [draftKey]: true },
        errors: { ...snapshot.errors, [draftKey]: false },
      });
      try {
        id = originalId || (await create()).id;
        const session = await api.prepare(id);
        if (disposed) return;
        if (!session.writable) throw new Error('Read-only session');
        mergeSession(session);
        watchGeneratedTitle(id);
        const messageId = uuid();
        update({
          messages: {
            ...snapshot.messages,
            [id]: [
              ...(snapshot.messages[id] || []).map((m) =>
                m.id === userMessageId ? { ...m, status: 'completed' as const } : m,
              ),
              {
                id: messageId,
                role: 'agent',
                content: '',
                status: 'running',
                created_at,
                tool_events: [],
              },
            ],
          },
          runs: {
            ...snapshot.runs,
            [id]: {
              messageId,
              sequence: 0,
              eventSequence: 0,
              progressSequence: 0,
              reconnecting: false,
            },
          },
        });
        const controller = new AbortController();
        controllers.set(id, controller);
        void runStream(id, messageId, controller, {
          text,
          userMessageId,
          presentation,
          locale,
          pageContext,
          fileIds: files.map((f) => f.id),
        });
      } catch (error) {
        // 尚未发出请求时回滚本地消息，保留草稿与附件供用户重试。
        update({
          messages: {
            ...snapshot.messages,
            [id]: (snapshot.messages[id] || []).filter((m) => m.id !== userMessageId),
          },
          drafts:
            reply === undefined
              ? { ...snapshot.drafts, [id]: snapshot.drafts[id] || draftText }
              : snapshot.drafts,
          files: { ...snapshot.files, [id]: [...files, ...(snapshot.files[id] || [])] },
          pastedTexts:
            reply === undefined
              ? {
                  ...snapshot.pastedTexts,
                  [id]: [...pastedTexts, ...(snapshot.pastedTexts[id] || [])],
                }
              : snapshot.pastedTexts,
          errors: { ...snapshot.errors, [id]: true },
        });
        throw error;
      } finally {
        preparing.delete(draftKey);
        preparing.delete(id);
        update({ loading: { ...snapshot.loading, [draftKey]: false, [id]: false } });
      }
    },
    async stop(id: string) {
      const run = snapshot.runs[id];
      if (!run) return;
      controllers.get(id)?.abort();
      controllers.delete(id);
      pendingUi.delete(run.messageId);
      pendingPageTasks.delete(run.messageId);
      patchMessage(id, run.messageId, (m) => ({
        ...m,
        status: 'cancelled',
        thinking_completed_at: new Date().toISOString(),
      }));
      finish(id);
      try {
        await api.stop(id, run.messageId);
      } catch {
        // The local cancellation is authoritative; the stream cannot resume after this point.
      }
    },
    async rename(id: string, title: string) {
      stopTitlePoll(id);
      mergeSession(await api.rename(id, title));
    },
    async pin(id: string, pinned: boolean) {
      mergeSession(await api.pin(id, pinned));
    },
    async remove(id: string) {
      stopTitlePoll(id);
      await api.remove(id);
      loadedSessionVersions.delete(id);
      controllers.get(id)?.abort();
      controllers.delete(id);
      finish(id);
      update({
        sessions: snapshot.sessions.filter((s) => s.id !== id),
        selectedId: snapshot.selectedId === id ? undefined : snapshot.selectedId,
      });
    },
    dispose() {
      disposed = true;
      ++listVersion;
      stopAllTitlePolls();
      api.dispose();
      controllers.forEach((c) => c.abort());
      controllers.clear();
      pendingUi.clear();
      pendingPageTasks.clear();
      receivedPageTasks.clear();
      preparing.clear();
      loadedSessionVersions.clear();
      listeners.clear();
      snapshot = empty();
    },
  };
}
export type AgentService = ReturnType<typeof createAgentService>;
